import { useCallback } from 'react'
import { MobileListItem } from '@mobileTypes'
import {
  useFetchAllMobileListItemsQuery,
  useCreateMobileListItemMutation,
  useUpdateMobileListItemMutation,
  useDeleteMobileListItemMutation,
  useCopyMobileListItemMutation,
} from '@mobileServices'

export const useMobileListItems = () => {
  const { data: listItems = [], isLoading: loading } = useFetchAllMobileListItemsQuery()

  const [createMobileListItem] = useCreateMobileListItemMutation()
  const [updateMobileListItem] = useUpdateMobileListItemMutation()
  const [deleteMobileListItem] = useDeleteMobileListItemMutation()
  const [copyMobileListItem] = useCopyMobileListItemMutation()

  const createListItem = useCallback(
    (itemData: Omit<MobileListItem, 'id'>): Promise<MobileListItem> =>
      createMobileListItem(itemData).unwrap(),
    [createMobileListItem]
  )

  const updateListItem = useCallback(
    (id: string, itemData: Partial<MobileListItem>): Promise<MobileListItem> => {
      const existing = listItems.find(i => String(i.id) === id)
      const merged: MobileListItem = {
        ...(existing ?? ({} as MobileListItem)),
        ...itemData,
      }
      return updateMobileListItem(merged).unwrap()
    },
    [updateMobileListItem, listItems]
  )

  const deleteListItem = useCallback(
    (id: string | number): void => {
      deleteMobileListItem(String(id))
    },
    [deleteMobileListItem]
  )

  const getListItemById = useCallback(
    (id: string): MobileListItem | undefined =>
      listItems.find(item => String(item.id) === id),
    [listItems]
  )

  const copyListItem = useCallback(
    async (id: string | number, newTitle: string, newInternalId: string): Promise<MobileListItem | null> => {
      try {
        return await copyMobileListItem({ id: String(id), title: newTitle, internalId: newInternalId }).unwrap()
      } catch {
        return null
      }
    },
    [copyMobileListItem]
  )

  return {
    listItems,
    loading,
    createListItem,
    updateListItem,
    deleteListItem,
    getListItemById,
    copyListItem,
  }
}
